<template>
  <div 
    class="premium-stat-card" 
    :class="{ 'is-clickable': to }"
    :style="cardStyles"
    @click="handleClick"
  >
    <div class="stat-top">
      <div class="stat-info">
        <div class="stat-label">{{ label }}</div>
        <div class="stat-value" v-if="!loading">{{ value }}</div>
        <div class="stat-skeleton-val" v-else></div>
      </div>
      <div class="stat-icon-wrap" :style="iconStyles">
        <v-icon :size="20" :color="color">{{ icon }}</v-icon>
      </div>
    </div>
    
    <div class="stat-bottom" v-if="trend || trendLabel">
      <div v-if="trend" class="stat-trend" :class="trend > 0 ? 'trend-up' : 'trend-down'">
        <v-icon size="12" :color="trend > 0 ? '#10B981' : '#EF4444'">
          {{ trend > 0 ? 'mdi-trending-up' : 'mdi-trending-down' }}
        </v-icon>
        <span>{{ Math.abs(trend) }}%</span>
      </div>
      <div v-if="trendLabel" class="trend-label">{{ trendLabel }}</div>
    </div>

    <!-- Accent progress line -->
    <div class="stat-progress-bg">
      <div class="stat-progress-fill" :style="progressStyles"></div>
    </div>

    <!-- Decorative glow -->
    <div class="stat-glow" :style="glowStyles"></div>
  </div>
</template>

<script>
export default {
  name: 'PremiumStatCard',
  props: {
    label:      { type: String, required: true },
    value:      { type: [String, Number], default: '0' },
    icon:       { type: String, default: 'mdi-chart-line' },
    color:      { type: String, default: '#3B82F6' },
    trend:      { type: Number, default: null }, // e.g. +12 or -5
    trendLabel: { type: String, default: '' },
    loading:    { type: Boolean, default: false },
    progress:   { type: Number, default: 100 }, // percentage 0-100
    to:         { type: [String, Object], default: null }, // vue-router route
  },

  methods: {
    handleClick() {
      if (this.to) {
        this.$router.push(this.to);
      }
    },
  },

  computed: {
    cardStyles() {
      return {
        '--accent': this.color,
      };
    },
    iconStyles() {
      return {
        background: `${this.color}15`, // 15% opacity version of the color
      };
    },
    progressStyles() {
      return {
        width: `${this.progress}%`,
        background: this.color,
      };
    },
    glowStyles() {
      return {
        background: `radial-gradient(circle at 80% 20%, ${this.color}12 0%, transparent 60%)`,
      };
    }
  }
};
</script>

<style scoped>
.premium-stat-card {
  position: relative;
  background: var(--surface, #ffffff);
  border: 1px solid var(--border, #E2E8F0);
  border-radius: 16px;
  padding: 20px;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 96px;
  min-height: 96px;
  cursor: default;
  box-shadow: rgba(0, 0, 0, 0.04) 0px 2px 10px;
}

.premium-stat-card.is-clickable {
  cursor: pointer;
}

.premium-stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(0,0,0,0.04);
  border-color: var(--accent);
}

.stat-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex: 1;
  position: relative;
  z-index: 2;
}

.stat-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.stat-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--tx3, #64748B);
  letter-spacing: 0.01em;
  line-height: 1.25;
  min-height: 32px;
  display: flex;
  align-items: flex-start;
}

.stat-value {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 28px;
  font-weight: 800;
  color: var(--tx, #0F172A);
  letter-spacing: -0.04em;
  line-height: 1.1;
}

.stat-skeleton-val {
  height: 32px;
  width: 80px;
  background: var(--surface2, #F1F5F9);
  border-radius: 6px;
  position: relative;
  overflow: hidden;
}
.stat-skeleton-val::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
  animation: shimmer 1.5s infinite;
}

.stat-icon-wrap {
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-bottom {
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  z-index: 2;
}

.stat-trend {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 6px;
}
.trend-up { background: rgba(16, 185, 129, 0.1); color: #10B981; }
.trend-down { background: rgba(239, 68, 68, 0.1); color: #EF4444; }

.trend-label {
  font-size: 11px;
  font-weight: 500;
  color: var(--tx4, #94A3B8);
}

.stat-progress-bg {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background: var(--surface2, #F1F5F9);
  z-index: 1;
}
.stat-progress-fill {
  height: 100%;
  border-radius: 0 2px 0 0;
  transition: width 1s ease-out;
}

.stat-glow {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  opacity: 0.6;
}

@media (max-width: 480px) {
  .premium-stat-card {
    padding: 14px;
    gap: 8px;
    height: 86px;
    min-height: 86px;
  }
  .stat-value { font-size: 22px; }
  .stat-label {
    font-size: 11px;
    min-height: 28px;
  }
  .stat-icon-wrap {
    width: 34px;
    height: 34px;
    flex-basis: 34px;
  }
  .stat-icon-wrap .v-icon { font-size: 16px !important; }
}

@keyframes shimmer {
  100% { transform: translateX(100%); }
}
</style>
